import { useRef } from 'react'

// Tracks the previously-visited route path so the recap pages can decide
// whether to (re)sync on entry.
//
// The recap views (prep / wrap / pivot) are flat sibling routes — switching
// between them unmounts one page and mounts another. We must sync the recap on
// entering the recap area from the budget page (the budget may have changed),
// but NOT when moving between recap views. Knowing the path we came FROM lets
// us tell those two cases apart.

const RECAP_PATH = /\/quote\/v1\/[^/]+\/(prep|wrap|pivot)$/

let previousPathname = ''

export function isRecapPath(pathname: string): boolean {
	return RECAP_PATH.test(pathname)
}

// Pure decision used by the hook: sync when we did NOT come from another recap
// view (i.e. came from the budget page, elsewhere, or a fresh load where the
// previous path is empty).
export function shouldSyncRecapOnEntry(previousPathname: string): boolean {
	return !isRecapPath(previousPathname)
}

// Called from the always-mounted MainLayout on every location change. Because
// the layout's effect runs AFTER the freshly-mounted route's render, a recap
// page captures the prior path (via useEnteredRecapFromOutside) before this
// overwrites it.
export function recordVisitedPath(pathname: string): void {
	previousPathname = pathname
}

// True when the recap page was entered from a non-recap page (e.g. budget) or a
// fresh page load — false when navigating between recap views. Captured once at
// mount so a later re-render (e.g. when the quote finishes loading) can't read a
// path that the layout has since updated.
export function useEnteredRecapFromOutside(): boolean {
	const enteredFromOutside = useRef(shouldSyncRecapOnEntry(previousPathname))
	return enteredFromOutside.current
}
